import React, {
  createContext,
  type ElementType,
  type ReactElement,
  type ReactNode,
  useContext,
} from 'react'
import ReactMarkdown from 'react-markdown'

import type { ColorToken } from '../themes'
import { InternalTextButton } from './TextButton'
import { Type } from './Type'

type MarkdownContextType = {
  textRenderer?: (text: string) => ReactElement | null
}
const MarkdownContext = createContext<MarkdownContextType>({
  textRenderer: undefined,
})
const useMarkdownContext = () => useContext(MarkdownContext)

type Props = {
  /**
   * Optional config to prevent a single word on the last line, this will force at least 2 words on the last line.
   */
  preventWidow?: boolean
  /**
   * Optional configuration to override components for react-markdown.
   * The `text` override is custom and will apply to all usage of text outside of headers, if you do not want the default of `<Type.Text size="S">`.
   */
  overrideRenderers?: { [k in string]: ElementType } & {
    text?: (text: string) => ReactElement | null
  }
  /**
   * Markdown source.
   */
  source: string
  /**
   * Optional plugins for react-markdown. Plugins extend functionality, for example you may use `remark-gfm` package
   * for github flavored markdown feature (eg table and strikethrough support).
   */
  plugins?: any[]
}

export function LinkRenderer({
  children,
  href,
  ...rest
}: {
  href: string
  children: ReactNode
}) {
  return (
    <InternalTextButton href={href} label={children} type="anchor" {...rest} />
  )
}

function ListItemRenderer({
  node,
  ...props
}: {
  // Note(@auster.chen): node doesn't have an exported type currently but it represents an AST object from react-markdown.
  node: any
  children: string
}) {
  return (
    <li>
      <TextRenderer {...props} />
    </li>
  )
}

function ParagraphRenderer({
  node,
  ...props
}: {
  // Note(@auster.chen): node doesn't have an exported type currently but it represents an AST object from react-markdown.
  node: any
  children: string
}) {
  return (
    <p>
      <TextRenderer {...props} />
    </p>
  )
}

export function TextRenderer({
  children,
  color,
  size = 'S',
}: {
  children: string
  size?: 'S' | 'M'
  color?: ColorToken | null
}) {
  const { textRenderer } = useMarkdownContext()

  if (textRenderer) {
    return textRenderer(children)
  }

  return (
    <Type.Text color={color} size={size}>
      {children}
    </Type.Text>
  )
}

const HEADING_TEXT_PROPS: Record<
  number,
  {
    renderer: (text: string) => ReactElement
  }
> = {
  1: {
    renderer: (text: string) => (
      <Type.Display element="h1" size="L">
        {text}
      </Type.Display>
    ),
  },
  2: {
    renderer: (text: string) => (
      <Type.Display element="h2" size="M">
        {text}
      </Type.Display>
    ),
  },
  3: {
    renderer: (text: string) => (
      <Type.Display element="h3" size="S">
        {text}
      </Type.Display>
    ),
  },
  4: {
    renderer: (text: string) => (
      <Type.Text element="h4" isBold size="M">
        {text}
      </Type.Text>
    ),
  },
  5: {
    renderer: (text: string) => (
      <Type.Text element="h5" isBold size="M">
        {text}
      </Type.Text>
    ),
  },
  6: {
    renderer: (text: string) => (
      <Type.Text element="h6" isBold size="M">
        {text}
      </Type.Text>
    ),
  },
}

function HeadingRenderer({
  children,
  level,
}: {
  children: string
  level: number
}) {
  const { renderer } = HEADING_TEXT_PROPS[level] || HEADING_TEXT_PROPS[1]

  return renderer(children)
}

/**
 * This component renders a Markdown string into the appropriate styled text.
 * It should only be used to render text provided by an API. See also the `RichText` component.
 *
 * For web-app `UniversalLinkWrapper` can be used to handle deeplinks.
 *
 * ```
 * <BentoMarkdown
 *  overrideRenderers={{ link: UniversalLinkWrapper }}
 * />
 *
 * // OR
 *
 * <LinkProvider linkComponent={UniversalLinkWrapper}>
 *  <BentoMarkdown />
 * </LinkProvider>
 * ```
 */
export function Markdown({
  overrideRenderers = {},
  preventWidow = false,
  plugins = [],
  source,
}: Props) {
  const { link, text, ...restRenderers } = overrideRenderers

  return (
    <MarkdownContext.Provider value={{ textRenderer: text }}>
      <ReactMarkdown
        plugins={plugins}
        renderers={{
          heading: HeadingRenderer,
          link: link ?? LinkRenderer,
          listItem: ListItemRenderer,
          paragraph: ParagraphRenderer,
          ...restRenderers,
        }}
        source={preventWidow ? widowFix(source) : source}
        transformLinkUri={uri => uriTransformer(uri)}
      />
    </MarkdownContext.Provider>
  )
}

/**
 * @summary a paragraph has a "widow" if there is a single word that spills
 * over to the next line alone. The standard fix for this is to replace the
 * space character, encoded as %20, with the non-breaking space character,
 * which is encoded as %A0. Here we are replacing the last instance of a
 * space character in the string with the non-breaking space character.
 * The motivation is that the two words will now be grouped such that
 * if the last word spills to the next line, then both words will.
 */
export function widowFix(str: string) {
  return str.replace(/\s([^\s<]+)\s*$/, '\u00A0$1')
}

// NOTE(alan.duan): This is based off the following function from react-markdown:
// https://github.com/remarkjs/react-markdown/blob/8ed18dafa8de638ab72a8db29f156f9fd9cf9e2e/src/uri-transformer.js
// but extended protocols to support robinhood deeplink.
const protocols = ['http', 'https', 'mailto', 'tel', 'robinhood']

export function uriTransformer(uri: string) {
  const url = (uri || '').trim()
  const first = url.charAt(0)

  if (first === '#' || first === '/') {
    return url
  }

  const colon = url.indexOf(':')
  if (colon === -1) {
    return url
  }

  let index = 0

  while (index < protocols.length) {
    const protocol = protocols[index]

    if (
      colon === protocol.length &&
      url.slice(0, protocol.length).toLowerCase() === protocol
    ) {
      return url
    }
    index += 1
  }

  index = url.indexOf('?')
  if (index !== -1 && colon > index) {
    return url
  }

  index = url.indexOf('#')
  if (index !== -1 && colon > index) {
    return url
  }

  // eslint-disable-next-line no-script-url
  return 'javascript:void(0)'
}
